// Port of api/fuel/app/classes/controller/api/monty/community/voting.php
import DB from 'offline/db'
import Constants from 'offline/constants'
import User from 'offline/model/user'
import PlatformVotingFeature from 'offline/model/platform/voting/feature'
import PlatformVotingVote from 'offline/model/platform/voting/vote'
import Util from 'offline/util'

function currentVoteTime() {
	return Math.floor(Date.now() / 1000) - (Constants.TIME_HOUR * 2)
}

function voteStamp(ts) {
	return Util.date('m.y', ts)
}

function voteLimit(user) {
	if (!user) return 0
	if (user.get_upgrade() === Constants.USER_UPGRADE_SUPPORTER) return 2
	if (user.get_upgrade() === Constants.USER_UPGRADE_POWER) return 5
	if (user.get_upgrade() === Constants.USER_UPGRADE_GROUP) return 10
	return 0
}

function deadline(ts) {
	return Util.mktime(20, 0, 0, [
		parseInt(Util.date('c', ts), 10),
		24,
		parseInt(Util.date('Y', ts), 10)
	])
}

function monthEnd(ts) {
	return Util.mktime(23, 59, 59, [
		parseInt(Util.date('c', ts), 10) + 1,
		0,
		parseInt(Util.date('Y', ts), 10)
	])
}

function stampToIndex(stamp) {
	var parts = String(stamp).split('.')
	return (parseInt(parts[1], 10) * 12) + parseInt(parts[0], 10)
}

function indexToStamp(index) {
	var year = Math.floor((index - 1) / 12)
	var month = index - (year * 12)
	var monthText = month < 10 ? ('0' + month) : String(month)
	var yearText = year < 10 ? ('0' + year) : String(year)
	return monthText + '.' + yearText
}

function featureData(feature, resultStamp) {
	var percent = 0

	if (resultStamp) {
		var cutoff = '09.25'
		var currentIndex = stampToIndex(resultStamp)
		var cutoffIndex = stampToIndex(cutoff)
		var activeIds = PlatformVotingFeature.collection({
			where: [
				['feature_status', '=', Constants.PLATFORM_VOTING_ACTIVE]
			]
		}).map(function(item) { return item.get_id() })
		var weightedTotal = 0
		var weightedOwn = 0
		var k = 0

		for (var idx = currentIndex; idx >= cutoffIndex; idx--, k++) {
			var monthStamp = indexToStamp(idx)
			var weight = Math.pow(0.5, k)
			var total = activeIds.length ? parseInt(PlatformVotingVote.sum('vote_count', {
				where: [
					['feature_id', 'in', activeIds],
					['vote_stamp', '=', monthStamp]
				]
			}), 10) || 0 : 0
			var own = parseInt(PlatformVotingVote.sum('vote_count', {
				where: [
					['feature_id', '=', feature.get_id()],
					['vote_stamp', '=', monthStamp]
				]
			}), 10) || 0

			weightedTotal += Math.floor(total * weight)
			weightedOwn += Math.floor(own * weight)
		}

		percent = weightedTotal > 0 ? (100 / weightedTotal) * weightedOwn : 0
	}

	return {
		id: parseInt(feature.get_id(), 10) || 0,
		name: feature.get_name(),
		description: feature.get_description(),
		status: parseInt(feature.get_status(), 10) || 0,
		date: parseInt(feature.get_date_won(), 10) || 0,
		percent: Math.round(percent * 10) / 10
	}
}

export default function(params) {
	var method = params.__offlineMethod || 'GET'
	var userId = params.sessionUserId || DB.meta('sync_user_id') || false
	var user = userId ? User.with_id(userId) : false
	var voteTime = currentVoteTime()
	var currentStamp = voteStamp(voteTime)
	var currentDeadline = deadline(voteTime)

	if (method === 'POST') {
		if (!user) {
			return Promise.reject({ error: 'Not available offline without a local session.', offline: true })
		}

		if (!voteLimit(user)) {
			return Promise.reject({ error: 'Voting is not available for this account.', offline: true })
		}

		if (currentDeadline < Math.floor(Date.now() / 1000)) {
			return Promise.reject({ error: 'Voting for this month has closed.', offline: true })
		}

		var limit = voteLimit(user)
		var featuresActive = PlatformVotingFeature.collection({
			where: [
				['feature_status', '=', Constants.PLATFORM_VOTING_ACTIVE]
			]
		})
		var inputVotes = params.votes || {}

		DB.run('DELETE FROM platform_voting_vote WHERE user_id = ? AND vote_stamp = ?', [
			user.get_id(), currentStamp
		])

		for (var fi = 0; fi < featuresActive.length; fi++) {
			var featureId = featuresActive[fi].get_id()
			var count = parseInt(inputVotes[featureId], 10) || 0

			if (count > 0) {
				if (count > limit) count = limit
				limit -= count

				DB.run('INSERT INTO platform_voting_vote (user_id, feature_id, vote_date, vote_count, vote_stamp) VALUES (?, ?, ?, ?, ?)', [
					user.get_id(), featureId, Math.floor(Date.now() / 1000), count, currentStamp
				])
			}
		}

		return DB.save().then(function() {
			var votesSaved = {}
			var rowsSaved = PlatformVotingVote.collection({
				where: [
					['user_id', '=', user.get_id()],
					['vote_stamp', '=', currentStamp]
				]
			})

			for (var si = 0; si < rowsSaved.length; si++) {
				votesSaved[rowsSaved[si].get_feature_id()] = parseInt(rowsSaved[si].get_count(), 10) || 0
			}

			return {
				session: { offline: true },
				data: {
					votes: votesSaved
				}
			}
		})
	}

	if (method !== 'GET') {
		return Promise.reject({ error: 'This action is not available offline.', offline: true })
	}

	var resultStamp = currentDeadline < Math.floor(Date.now() / 1000) ? currentStamp : false
	var features = PlatformVotingFeature.collection({
		where: [
			['feature_status', 'in', [
				Constants.PLATFORM_VOTING_ACTIVE,
				Constants.PLATFORM_VOTING_WINNER
			]],
			['feature_date_added', '<=', monthEnd(voteTime)]
		]
	}).map(function(feature) {
		return featureData(feature, resultStamp)
	})

	var votes = {}
	if (user) {
		var rows = PlatformVotingVote.collection({
			where: [
				['user_id', '=', user.get_id()],
				['vote_stamp', '=', currentStamp]
			]
		})
		for (var i = 0; i < rows.length; i++) {
			votes[rows[i].get_feature_id()] = parseInt(rows[i].get_count(), 10) || 0
		}
	}

	return Promise.resolve({
		session: { offline: true },
		data: {
			voting: features,
			votes: votes,
			limit: voteLimit(user),
			month: Util.date('F', voteTime),
			deadline: currentDeadline
		}
	})
}
